import { useEffect, useState } from "react";
import {
  getSalesByMonth, getTarget, getBanners, getHolidays, getOvertimeByMonth,
  getAttendanceByMonth, getAllBonusPrograms, getEventBonuses,
  getIncomeHistoryForPromoter, getPayrollAdjustment,
} from "../../firebase/services";
import { calcSalesBonus, calcHighendBonus, calcPayrollDeductions, computeMonthlyPay, promoterForMonth } from "../../firebase/schema";
import { usePayrollConfig } from "../../hooks/usePayrollConfig";
import { useAuth } from "../../hooks/useAuth";
import SalaryBreakdown from "../../components/SalaryBreakdown";
import { format, subMonths } from "date-fns";
import { useLanguage } from "../../hooks/useLanguage";
import BannerSlider from "../../components/BannerSlider";

const MONTH = format(new Date(), "yyyy-MM");

export default function PromoterHome() {
  const { t } = useLanguage();
  const { user, profile } = useAuth();
  const [stats, setStats] = useState({ sold: 0, highend: 0, target: 0, heTarget: 0 });
  const [banners, setBanners] = useState([]);
  const { cfgFor, loaded: cfgLoaded } = usePayrollConfig();
  const bonusCfg = cfgFor(MONTH).bonus;   // dự báo thưởng tháng này
  const [rangeMonths, setRangeMonths] = useState(6);
  const [incomeRows, setIncomeRows] = useState([]);
  const [loadingIncome, setLoadingIncome] = useState(false);
  const [breakdownRow, setBreakdownRow] = useState(null);

  useEffect(() => {
    getBanners().then(setBanners).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    Promise.all([getSalesByMonth(MONTH, user.uid), getTarget(user.uid, MONTH)]).then(([sales, t]) => {
      setStats({ sold: sales.length, highend: sales.filter(s => s.isHighend).length, target: t?.sellTarget || 0, heTarget: t?.highendTarget || 0 });
    });
  }, [user]);

  // Lịch sử thu nhập theo tháng
  useEffect(() => {
    if (!user || !profile || !cfgLoaded) return;
    let cancelled = false;
    const run = async () => {
      setLoadingIncome(true);
      const programs = await getAllBonusPrograms().catch(() => []);
      const history = await getIncomeHistoryForPromoter(user.uid).catch(() => []);
      const holidays = await getHolidays().catch(() => []);
      const histByMonth = {};
      history.forEach(h => { histByMonth[h.month] = h; });
      const months = Array.from({ length: rangeMonths }, (_, i) => format(subMonths(new Date(), i), "yyyy-MM"));
      const rows = await Promise.all(months.map(async (m) => {
        // Ưu tiên dữ liệu import (tháng cũ)
        const h = histByMonth[m];
        if (h) {
          return {
            month: m, sold: h.sold || 0, highendSold: h.highendSold || 0,
            income: h.incomeNet ?? h.incomeGross ?? 0, gross: h.incomeGross ?? 0,
            breakdown: h.breakdown || null, imported: true, isCurrent: m === MONTH,
          };
        }
        const [atts, sales, tgt, events, adj, ot] = await Promise.all([
          getAttendanceByMonth(user.uid, m).catch(() => []),
          getSalesByMonth(m, user.uid).catch(() => []),
          getTarget(user.uid, m).catch(() => null),
          getEventBonuses(m, [user.uid]).catch(() => []),
          getPayrollAdjustment(user.uid, m).catch(() => null),
          getOvertimeByMonth(m, user.uid).catch(() => []),
        ]);
        const cfg = cfgFor(m);   // quy tắc áp dụng cho đúng tháng này
        const pm = promoterForMonth(profile, m);   // lương cơ bản / phụ cấp của đúng tháng này
        const pay = computeMonthlyPay({
          promoter: pm, month: m, cfg,
          attendance: atts, sales, target: tgt, programs, events,
          adjustment: adj, holidays, overtime: ot,
        });
        const ded = calcPayrollDeductions({
          totalIncome: pay.total, nonTaxable: pay.nonTaxable,
          baseSalary: pm.baseSalary || 0,
          allowance: pm.allowance || 0,
          dependents: profile.dependents || 0,
          startDate: profile.startDate || null,
          month: m, cfg: cfg.tax,
        });
        return {
          ...pay, month: m, cfg,
          baseSalary: pm.baseSalary || 0,
          income: ded.net, gross: pay.total, ded,
          isCurrent: m === MONTH,
        };
      }));
      if (!cancelled) { setIncomeRows(rows); setLoadingIncome(false); }
    };
    run();
    return () => { cancelled = true; };
  }, [user, profile, rangeMonths, cfgLoaded, cfgFor]);

  const hitRate = stats.target > 0 ? stats.sold / stats.target : 0;
  const dsBonus = calcSalesBonus(stats.sold, stats.target, bonusCfg?.salesTiers);
  const heBonus = calcHighendBonus(stats.highend, stats.heTarget, bonusCfg?.highendConfig);
  const pct = Math.min(100, Math.round(hitRate * 100));
  const hePct = stats.heTarget > 0 ? Math.min(100, Math.round(stats.highend / stats.heTarget * 100)) : 0;

  return (
    <>
      <BannerSlider banners={banners} />
      <div className="page-header">
        <div>
          <h1>{t("dash.hello")}, {profile?.name} 👋</h1>
          <p>Shop: {profile?.shopName} · {t("x.month")} {MONTH.slice(5)}/{MONTH.slice(0,4)}</p>
        </div>
      </div>
      <div className="metrics-grid cols-3">
        <div className="metric-card">
          <div className="metric-label">{t("home.this_month")}</div>
          <div className="metric-value">{stats.sold} / {stats.target}</div>
          <div className="metric-sub">Hit rate: {pct}%</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Highend</div>
          <div className="metric-value">{stats.highend} / {stats.heTarget}</div>
          <div className="metric-sub">{t("home.exp_bonus")}: {heBonus.toLocaleString("vi-VN")}đ</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">{t("home.exp_ds")}</div>
          <div className="metric-value">{dsBonus > 0 ? (dsBonus/1e6).toFixed(1)+"M" : "—"}</div>
          <div className="metric-sub">{hitRate >= 0.8 ? t("home.reached") : `${t("home.need_more")} ${Math.max(0, Math.ceil(stats.target*0.8 - stats.sold))} ${t("home.more_units")}`}</div>
        </div>
      </div>
      <div className="card">
        <div className="card-title">{t("home.progress")} {MONTH.slice(5)}</div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#888", marginBottom:6 }}>
            <span>Sell-out: {stats.sold}/{stats.target} {t("home.units")}</span><span>{pct}%</span>
          </div>
          <div className="progress-wrap" style={{ height:12 }}>
            <div className="progress-bar" style={{ width: pct+"%", background: hitRate>=1.2?"#534AB7":hitRate>=1.0?"#C8152A":hitRate>=0.8?"#1D9E75":"#EF9F27" }} />
          </div>
        </div>
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#888", marginBottom:6 }}>
            <span>Highend: {stats.highend}/{stats.heTarget} {t("home.units")}</span><span>{hePct}%</span>
          </div>
          <div className="progress-wrap" style={{ height:12 }}>
            <div className="progress-bar" style={{ width: hePct+"%", background:"#C8152A" }} />
          </div>
        </div>
        {dsBonus > 0 && (
          <div style={{ marginTop:14, padding:"10px 14px", background:"#EAF3DE", borderRadius:8, fontSize:13, color:"#3B6D11" }}>
            {t("salary.sales_bonus")}: {dsBonus.toLocaleString("vi-VN")}đ + {t("salary.highend_bonus")}: {heBonus.toLocaleString("vi-VN")}đ
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">{t("inc.title")}</div>
          <select value={rangeMonths} onChange={e => setRangeMonths(Number(e.target.value))} style={{ width: 130, fontSize: 13 }}>
            <option value={3}>{t("inc.m3")}</option>
            <option value={6}>{t("inc.m6")}</option>
            <option value={12}>{t("inc.m12")}</option>
          </select>
        </div>
        {loadingIncome ? (
          <div style={{ color: "#888", padding: 16, textAlign: "center" }}>{t("inc.loading")}</div>
        ) : (
          <table className="table-card">
            <thead>
              <tr><th>{t("x.month")}</th><th>{t("inc.sold")}</th><th>Highend</th><th style={{ textAlign: "right" }}>Gross</th><th style={{ textAlign: "right" }}>{t("inc.net")}</th></tr>
            </thead>
            <tbody>
              {incomeRows.map(r => (
                <tr key={r.month}
                  onClick={() => setBreakdownRow(r)}
                  style={{ cursor: "pointer" }}>
                  <td data-label="Tháng">
                    {r.month.slice(5)}/{r.month.slice(0, 4)}
                    <span style={{ fontSize: 11, color: "#2563eb", marginLeft: 6, textDecoration: "underline" }}>{t("inc.detail")}</span>
                    {!r.imported && r.isCurrent && <span style={{ fontSize: 11, color: "#EF9F27", marginLeft: 6 }}>{t("inc.provisional")}</span>}
                  </td>
                  <td data-label="Đã bán">{r.sold}</td>
                  <td data-label="Highend">{r.highendSold}</td>
                  <td data-label="Gross" style={{ textAlign: "right", color: "#888" }}>{r.gross != null ? (r.gross || 0).toLocaleString("vi-VN") + " đ" : "—"}</td>
                  <td data-label="Thực nhận" style={{ textAlign: "right", fontWeight: 500 }}>{r.income.toLocaleString("vi-VN")} đ</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={{ fontWeight: 500, paddingTop: 10 }}>{t("inc.total_range")} ({rangeMonths})</td>
                <td style={{ textAlign: "right", fontWeight: 600, paddingTop: 10 }}>
                  {incomeRows.reduce((a, r) => a + r.income, 0).toLocaleString("vi-VN")} đ
                </td>
              </tr>
            </tfoot>
          </table>
        )}
        <div style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>
          {t("inc.footnote")}
        </div>
      </div>

      {breakdownRow && (
        <div className="modal-overlay" onClick={() => setBreakdownRow(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{t("inc.modal_title")} — {breakdownRow.month.slice(5)}/{breakdownRow.month.slice(0, 4)}</div>
              <button className="btn btn-sm" onClick={() => setBreakdownRow(null)}>✕</button>
            </div>
            {(() => {
              const b = breakdownRow.breakdown;
              const money = (v) => (Number(v || 0)).toLocaleString("vi-VN") + " đ";
              // Tháng tính live → hiện các khoản lương/thưởng
              if (!breakdownRow.imported && breakdownRow.ded) {
                return (
                  <SalaryBreakdown
                    month={breakdownRow.month}
                    taxCfg={breakdownRow.cfg.tax}
                    workCfg={breakdownRow.cfg.work}
                    notice={breakdownRow.isCurrent ? (
                      <div className="alert alert-info" style={{ fontSize: 12, marginBottom: 8 }}>
                        {t("inc.provisional_note")}
                      </div>
                    ) : null}
                    row={breakdownRow}
                  />
                );
              }
              if (!b || Object.keys(b).length === 0) {
                return (
                  <div className="salary-breakdown">
                    <div className="alert alert-info" style={{ fontSize: 12, marginBottom: 8 }}>
                      {t("inc.no_breakdown")}
                    </div>
                    <div className="salary-row" style={{ fontWeight: 600 }}><span>{t("inc.gross_lb")}</span><span>{money(breakdownRow.gross)}</span></div>
                    <div className="salary-row" style={{ fontWeight: 700 }}><span>{t("inc.net")}</span><span>{money(breakdownRow.income)}</span></div>
                  </div>
                );
              }
              const companyTotal = (b.companySI || 0) + (b.companyHI || 0) + (b.companyUI || 0) + (b.tradeUnion || 0) + (b.pai || 0);
              const empTotal = (b.empSI || 0) + (b.empHI || 0) + (b.empUI || 0);
              const showTax = (breakdownRow.gross || 0) > 15500000;
              const secHeader = { fontWeight: 600, fontSize: 13, color: "#444", margin: "10px 0 2px" };
              const sub = { color: "#777", fontSize: 13 };
              return (
                <div className="salary-breakdown">
                  <div className="salary-row" style={{ fontWeight: 700, fontSize: 15 }}>
                    <span>💰 {t("inc.gross_lb")}</span><span>{money(breakdownRow.gross)}</span>
                  </div>

                  <div style={secHeader}>{t("inc.company_ins")}</div>
                  <div className="salary-row" style={sub}><span>BHXH (17,5%)</span><span>{money(b.companySI)}</span></div>
                  <div className="salary-row" style={sub}><span>BHYT (3%)</span><span>{money(b.companyHI)}</span></div>
                  <div className="salary-row" style={sub}><span>BH thất nghiệp (1%)</span><span>{money(b.companyUI)}</span></div>
                  <div className="salary-row" style={sub}><span>Công đoàn (2%)</span><span>{money(b.tradeUnion)}</span></div>
                  <div className="salary-row" style={sub}><span>Bảo hiểm tai nạn (PAI)</span><span>{money(b.pai)}</span></div>
                  <div className="salary-row" style={{ fontSize: 13 }}><span>{t("inc.total_company")}</span><span>{money(companyTotal)}</span></div>

                  <div style={secHeader}>{t("inc.emp_ins")}</div>
                  <div className="salary-row" style={sub}><span>BHXH (8%)</span><span>{money(b.empSI)}</span></div>
                  <div className="salary-row" style={sub}><span>BHYT (1,5%)</span><span>{money(b.empHI)}</span></div>
                  <div className="salary-row" style={sub}><span>BH thất nghiệp (1%)</span><span>{money(b.empUI)}</span></div>
                  <div className="salary-row" style={{ fontWeight: 600, color: "#A32D2D" }}><span>{t("inc.total_emp")}</span><span>− {money(empTotal)}</span></div>

                  {showTax ? (
                    <>
                      <div style={secHeader}>{t("inc.tax_section")}</div>
                      <div className="salary-row" style={sub}><span>{t("inc.taxable")}</span><span>{money(b.taxedIncome)}</span></div>
                      <div className="salary-row" style={{ fontWeight: 600, color: "#A32D2D" }}><span>{t("inc.pit")}</span><span>− {money(b.pit)}</span></div>
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: "#888", margin: "10px 0 0" }}>
                      {t("inc.no_tax")}
                    </div>
                  )}

                  <div className="salary-row" style={{ fontWeight: 700, fontSize: 15, borderTop: "1px solid #eee", paddingTop: 10, marginTop: 8, color: "#1D9E75" }}>
                    <span>✅ {t("inc.net")}</span><span>{money(breakdownRow.income)}</span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}
